import _debounce from 'lodash/debounce';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Slider, SliderLabel, token, StyledSliderTrack, StyledSliderThumb } from '@zillow/constellation';
import styled from 'styled-components';
import { event } from '@zillow/universal-analytics';
import AffordabilityDescription from './AffordabilityDescription.js';
import AffordabilityGraphic from './AffordabilityGraphic.js';
import { useLastValidAffordabilityValues, AFFORDABILITY_DTI_MAX, FieldConfig } from '../AffordabilityCalculator.utils.js';
import { getWholeDollars, getCommaSeparatedNumber } from '../../../utils/zhl-calculator.js';
import { trackLabel } from '../trackLabel.js';

var handleFieldChange = _debounce(function (label) {
  var cmsInfo = {
    block_type_cd: 'slider',
    block_id: 'afffordability_calc',
    block_displayed_txt: label
  };
  sendInteractionEvent(cmsInfo);
}, 3000);
var sendInteractionEvent = function sendInteractionEvent(cmsInfo) {
  var newLaneData = {
    envelope: {
      event_type_id: '2056',
      event_type_version_id: '2',
      event_template_id: '126',
      event_template_version_id: '1'
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'cms',
      trigger_source_nm: 'cms_slider',
      trigger_object_nm: 'no_trigger_object'
    },
    semantic: {
      semantic_event_nm: 'cms_engagement'
    },
    cms_info: cmsInfo
  };
  event(newLaneData);
};
var StyledSlider = /*#__PURE__*/styled(Slider).withConfig({
  componentId: "sc-15ej76z-0"
})(["", "{display:inline-flex;top:auto;bottom:-50px;white-space:nowrap;&::after{border-bottom:8px solid ", ";border-top:none;top:-32px;bottom:auto;margin-top:-8px;margin-bottom:0;}}", ":first-child{background-color:", ";}"], SliderLabel, token('colors.brand'), StyledSliderTrack, function (_ref) {
  var aggressive = _ref.aggressive;
  return aggressive ? token('colors.caution') : token('colors.green300');
});
var debouncedTrackSliderEdit = _debounce(function () {
  return trackLabel('Slider Edit');
}, 1000);
function AffordabilityVisual() {
  var form = useFormContext();
  var lastValidValues = useLastValidAffordabilityValues(form);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AffordabilityDescription, {
    aggressive: lastValidValues.calculations.aggressive,
    affordabilityByDTI: lastValidValues.calculations.affordability.affordabilityByDTI
  }), /*#__PURE__*/React.createElement(AffordabilityGraphic, {
    customPayment: lastValidValues.fields.customPayment,
    monthlyPayment: lastValidValues.fields.monthlyPayment,
    maximumPayment: lastValidValues.fields.maximumPayment,
    dti: lastValidValues.fields.dti
  }), /*#__PURE__*/React.createElement(StyledSlider, {
    aggressive: lastValidValues.calculations.aggressive,
    id: "affordability-slider",
    min: 0,
    max: lastValidValues.fields.customPayment ? lastValidValues.fields.maximumPayment : AFFORDABILITY_DTI_MAX,
    value: lastValidValues.fields.customPayment ? lastValidValues.fields.monthlyPayment : lastValidValues.fields.dti,
    ariaValuetext: getWholeDollars(lastValidValues.calculations.affordability.monthlyPayment) + " per month",
    ariaLabel: "Monthly payment"
    // The default `renderThumb` prop hard-codes `aria-labelledby`
    // for `StyledSliderThumb` - since we provide `ariaLabel`
    // instead, we omit `aria-labelledby`.
    ,
    renderThumb: function renderThumb(thumbProps) {
      return /*#__PURE__*/React.createElement(StyledSliderThumb, thumbProps, /*#__PURE__*/React.createElement(SliderLabel, {
        id: thumbProps.key
      }, getWholeDollars(lastValidValues.calculations.affordability.monthlyPayment) + "/mo"));
    },
    trackClassName: "affordability-track",
    onChange: function onChange(value) {
      form.setValue(lastValidValues.fields.customPayment ? FieldConfig.monthlyPayment.name : FieldConfig.dti.name, getCommaSeparatedNumber(value), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
      debouncedTrackSliderEdit();
      handleFieldChange('Affordability slider');
    }
  }));
}

export { AffordabilityVisual as default };
